import { API_URL } from "../_config/config.const";

/**
 * Pseudonymous, cookieless customer-journey tracking.
 *
 * Guarantees (these are what keep it from ever interfering with a booking):
 *  - Every public function is wrapped in try/catch and returns void. Nothing here can throw
 *    into a React render, a submit handler or a service call.
 *  - No React state. Events go into a plain array; nothing re-renders because of tracking.
 *  - Sending is fire-and-forget (sendBeacon / fetch keepalive). Results are never awaited and
 *    rejections are swallowed. The booking API calls are separate requests on separate promises.
 *  - Payload is text/plain so it is a CORS "simple request" (no preflight) and so Safari/iOS
 *    accept it via navigator.sendBeacon (they reject non-safelisted beacon content types).
 *  - Kill switch: REACT_APP_JOURNEY_TRACKING=false turns every function into a no-op.
 *
 * Temporary debug: field_value / field_blur meta may include typed values (v=...).
 * Purge with dbo.Purge_JourneyEvents when the investigation is done.
 */

const STORAGE_KEY = "fabs_journey_id";
const FLUSH_DELAY_MS = 3000;
const MAX_QUEUE = 80;
const META_MAX = 500;

export type JourneyStepName =
    | "service_details"
    | "initial_details"
    | "appointment_picker"
    | "customer_details"
    | "payment"
    | "confirmed";

interface JourneyEventPayload {
    name: string;
    step?: string | null;
    field?: string | null;
    meta?: string | null;
    appointmentId?: number | null;
    clientTime: string;
}

let memoryJourneyId: string | null = null;
let queue: JourneyEventPayload[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listenersAttached = false;
let deviceCache: string | null = null;
let currentStep: string | null = null;
let currentAppointmentId: number | null = null;
// After startNewJourney(), the details-step unmount should not write step_leave onto the new id.
let skipNextDetailsLeave = false;

export function isJourneyTrackingEnabled(): boolean {
    try {
        return process.env.REACT_APP_JOURNEY_TRACKING !== "false";
    } catch {
        return false;
    }
}

function generateId(): string {
    try {
        const c: any = typeof crypto !== "undefined" ? crypto : null;
        if (c && typeof c.randomUUID === "function") {
            return c.randomUUID();
        }
        if (c && typeof c.getRandomValues === "function") {
            const bytes = new Uint8Array(16);
            c.getRandomValues(bytes);
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            let hex = "";
            for (let i = 0; i < bytes.length; i++) {
                hex += (bytes[i] < 16 ? "0" : "") + bytes[i].toString(16);
            }
            return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
        }
    } catch {
        // fall through to Math.random
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
        const r = (Math.random() * 16) | 0;
        const v = ch === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Per-browser-session id. sessionStorage survives back/forward-cache restores on iOS but is
 * dropped when the tab is closed. Falls back to memory when storage is blocked (private mode
 * quirks, locked-down WebViews).
 */
export function getJourneyId(): string | null {
    if (!isJourneyTrackingEnabled()) return null;

    try {
        if (memoryJourneyId) return memoryJourneyId;

        let id: string | null = null;
        try {
            id = window.sessionStorage.getItem(STORAGE_KEY);
        } catch {
            id = null;
        }

        if (!id) {
            id = generateId();
            try {
                window.sessionStorage.setItem(STORAGE_KEY, id);
            } catch {
                // storage blocked - memory only for this page load
            }
        }

        memoryJourneyId = id;
        return id;
    } catch {
        return null;
    }
}

/**
 * Starts a new funnel in this tab (back from details, booking complete, timer expired).
 * Flushes the old id first so back_click / complete_success stay on the attempt they belong to.
 */
export function startNewJourney(): void {
    try {
        if (!isJourneyTrackingEnabled()) return;

        flushJourneyEvents(false);
        skipNextDetailsLeave = true;
        currentAppointmentId = null;

        const id = generateId();
        memoryJourneyId = id;
        try {
            window.sessionStorage.setItem(STORAGE_KEY, id);
        } catch {
            // memory only
        }
    } catch {
        // analytics only
    }
}

/** True once after startNewJourney(); used by the details-step unmount so leave stays on the old id. */
export function consumeSkipDetailsLeave(): boolean {
    try {
        if (!skipNextDetailsLeave) return false;
        skipNextDetailsLeave = false;
        return true;
    } catch {
        return false;
    }
}

/** Coarse device class only (no raw user agent is ever sent). */
function getDevice(): string {
    if (deviceCache) return deviceCache;
    try {
        const ua = (navigator && navigator.userAgent) || "";
        const isIos = /iPhone|iPad|iPod/i.test(ua)
            || (/Macintosh/i.test(ua) && typeof document !== "undefined" && "ontouchend" in document); // iPadOS desktop UA
        const isAndroid = /Android/i.test(ua);
        const isInApp = /FBAN|FBAV|Instagram|Line\/|Twitter|TikTok|Snapchat/i.test(ua);
        const base = isIos ? "ios" : isAndroid ? "android" : /Mobi/i.test(ua) ? "mobile" : "desktop";
        deviceCache = isInApp ? `${base}-inapp` : base;
    } catch {
        deviceCache = "unknown";
    }
    return deviceCache;
}

export function setJourneyStep(step: JourneyStepName | null): void {
    try {
        currentStep = step;
    } catch {
        // no-op
    }
}

function clamp(value: string | null | undefined, max: number): string | null {
    if (value == null) return null;
    const s = String(value);
    return s.length > max ? s.slice(0, max) : s;
}

function enqueue(name: string, opts?: { step?: string | null; field?: string | null; meta?: string | null; appointmentId?: number | null }): void {
    if (queue.length >= MAX_QUEUE) {
        queue.shift();
    }
    queue.push({
        name,
        step: clamp(opts?.step ?? currentStep, 50),
        field: clamp(opts?.field, 100),
        meta: clamp(opts?.meta, META_MAX),
        appointmentId: opts?.appointmentId ?? currentAppointmentId,
        clientTime: new Date().toISOString()
    });
}

function scheduleFlush(): void {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
        flushTimer = null;
        flushJourneyEvents(false);
    }, FLUSH_DELAY_MS);
}

/**
 * Send whatever is queued. Never awaited, never throws.
 * @param unloading true when called from pagehide/visibilitychange so keepalive is used.
 */
export function flushJourneyEvents(unloading: boolean = false): void {
    try {
        if (flushTimer) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
        if (queue.length === 0) return;

        const journeyId = getJourneyId();
        if (!journeyId) {
            queue = [];
            return;
        }

        const events = queue.splice(0, MAX_QUEUE);
        const body = JSON.stringify({ journeyId, device: getDevice(), events });
        const url = `${API_URL}/journey/events`;

        let sent = false;
        try {
            // String body => text/plain;charset=UTF-8, which is CORS-safelisted and accepted by Safari/iOS.
            if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
                sent = navigator.sendBeacon(url, body);
            }
        } catch {
            sent = false;
        }

        if (!sent && typeof fetch === "function") {
            fetch(url, {
                method: "POST",
                headers: { "Content-Type": "text/plain" },
                body,
                keepalive: unloading,
                credentials: "omit"
            }).catch(() => { /* analytics only */ });
        }
    } catch {
        // analytics only
    }
}

function ensureListeners(): void {
    if (listenersAttached || typeof document === "undefined" || typeof window === "undefined") return;
    listenersAttached = true;

    // iOS Safari does not reliably fire beforeunload/unload; visibilitychange + pagehide are the
    // recommended signals and are the ones that fire when a tab is backgrounded or evicted.
    document.addEventListener("visibilitychange", () => {
        try {
            if (document.visibilityState === "hidden") {
                enqueue("tab_hidden");
                flushJourneyEvents(true);
            } else {
                enqueue("tab_visible");
                scheduleFlush();
            }
        } catch {
            // analytics only
        }
    });

    window.addEventListener("pagehide", () => {
        try {
            enqueue("page_hide");
            flushJourneyEvents(true);
        } catch {
            // analytics only
        }
    });
}

/**
 * Record an event. Safe to call from anywhere (render, handlers, promise callbacks).
 */
export function trackJourneyEvent(
    name: string,
    opts?: { step?: string | null; field?: string | null; meta?: string | null; appointmentId?: number | null }
): void {
    try {
        if (!isJourneyTrackingEnabled()) return;
        ensureListeners();
        if (opts?.appointmentId != null) currentAppointmentId = opts.appointmentId;
        enqueue(name, opts);
        if (queue.length >= MAX_QUEUE) {
            flushJourneyEvents(false);
        } else {
            scheduleFlush();
        }
    } catch {
        // analytics only
    }
}

/**
 * Resolve the logical field name for a focus/blur/change target inside the details form.
 * Returns null for anything that is not a form control we care about.
 */
export function getTrackedFieldName(target: EventTarget | null): string | null {
    try {
        const el = target as HTMLElement | null;
        if (!el || typeof el.tagName !== "string") return null;

        const tag = el.tagName.toUpperCase();
        if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") return null;

        const name = (el as HTMLInputElement).name;
        if (name === "phone") return "mobilePhoneNo";
        if (name) return name;

        if (typeof el.closest === "function" && el.closest(".dateOfBirthPicker")) return "dateOfBirth";

        return null;
    } catch {
        return null;
    }
}

/** Current control value for debug capture. Never throws. */
export function getTrackedFieldValue(target: EventTarget | null): string {
    try {
        const el = target as HTMLInputElement | null;
        if (!el) return "";
        if (el.type === "checkbox") return el.checked ? "true" : "false";
        if (typeof el.value === "string") return el.value;
        return "";
    } catch {
        return "";
    }
}

export function valueMeta(opts: { ms?: number; reason?: string; value?: string | null }): string {
    try {
        const bits: string[] = [];
        if (opts.ms != null && !isNaN(opts.ms)) bits.push(`ms=${opts.ms}`);
        if (opts.reason) bits.push(`reason=${opts.reason}`);
        const raw = opts.value == null ? "" : String(opts.value);
        if (raw.length === 0) {
            bits.push("empty=1");
        } else {
            const room = Math.max(0, META_MAX - bits.join(";").length - 3);
            bits.push(`v=${raw.length <= room ? raw : raw.slice(0, room)}`);
        }
        const joined = bits.join(";");
        return joined.length > META_MAX ? joined.slice(0, META_MAX) : joined;
    } catch {
        return "";
    }
}
